<template>
  <div class="relative flex min-h-screen overflow-hidden isolate">
    <!-- Gradient Background Layer -->
    <div class="absolute inset-0 -z-10 bg-gradient-to-br from-slate-900 via-indigo-900 to-sky-800"></div>
    <!-- Decorative blurred blobs -->
    <div class="absolute bg-indigo-500 rounded-full pointer-events-none -top-32 -left-32 h-96 w-96 opacity-30 blur-3xl">
    </div>
    <div
      class="pointer-events-none absolute bottom-0 right-0 h-[30rem] w-[30rem] rounded-full bg-sky-500 opacity-30 blur-3xl">
    </div>

    <div class="relative z-10 flex flex-col w-full lg:flex-row">
      <!-- Left / Branding Panel -->
      <div class="hidden w-full px-10 py-16 lg:flex lg:w-1/2 xl:w-7/12">
        <div class="flex flex-col justify-center w-full max-w-xl mx-auto text-indigo-50">
          <div class="flex items-center mb-8 space-x-4">
            <div
              class="flex items-center justify-center p-3 shadow-lg rounded-xl ring-2 ring-white/30 backdrop-blur-sm bg-white/10">
              <ApplicationLogo class="w-14 h-14" />
            </div>
            <div>
              <h1 class="text-3xl font-bold leading-tight tracking-tight">
                {{ $page.props.apps?.nama_aplikasi || 'Panel Webmaster' }}
              </h1>
              <p class="mt-1 text-sm font-medium text-indigo-100/90">
                Fakultas Ekonomi dan Bisnis
              </p>
            </div>
          </div>
          <h2
            class="mb-4 text-4xl font-bold leading-tight text-transparent bg-clip-text bg-gradient-to-br from-indigo-200 to-sky-200">
            Selamat Datang Kembali
          </h2>
          <p class="mb-8 text-base leading-relaxed text-indigo-50">
            Silakan masuk untuk mengelola konten dan pengaturan sistem. Keamanan akun Anda
            adalah prioritas kami.
          </p>

          <div class="flex items-center space-x-6">
            <div class="flex -space-x-3">
              <div
                class="flex items-center justify-center w-10 h-10 font-bold text-white rounded-full opacity-0 ring-2 ring-white/40 bg-gradient-to-br from-indigo-400 to-indigo-600 animate-profile"
                style="animation-delay: 0ms">
                A
              </div>
              <div
                class="flex items-center justify-center w-10 h-10 font-bold text-white rounded-full opacity-0 ring-2 ring-white/40 bg-gradient-to-br from-sky-400 to-sky-600 animate-profile"
                style="animation-delay: 200ms">
                I
              </div>
              <div
                class="flex items-center justify-center w-10 h-10 font-bold text-white rounded-full opacity-0 ring-2 ring-white/40 bg-gradient-to-br from-violet-400 to-violet-600 animate-profile"
                style="animation-delay: 400ms">
                U
              </div>
              <div
                class="flex items-center justify-center w-10 h-10 font-bold text-white rounded-full opacity-0 ring-2 ring-white/40 bg-gradient-to-br from-pink-400 to-pink-600 animate-profile"
                style="animation-delay: 600ms">
                E
              </div>
              <div
                class="flex items-center justify-center w-10 h-10 font-bold text-white rounded-full opacity-0 ring-2 ring-white/40 bg-gradient-to-br from-amber-400 to-amber-600 animate-profile"
                style="animation-delay: 800ms">
                O
              </div>
            </div>
            <p class="text-sm text-indigo-50">Tim Humas</p>
          </div>
        </div>
      </div>

      <!-- Right / Form Panel -->
      <div class="flex items-center justify-center w-full px-6 py-12 sm:px-8 lg:w-1/2 xl:w-5/12">
        <div class="w-full max-w-md">
          <div class="p-8 shadow-2xl backdrop-blur-md bg-white/10 ring-1 ring-white/20 rounded-2xl">
            <div class="mb-8 text-center">
              <h3 class="mb-2 text-2xl font-bold tracking-tight text-white">Masuk ke Panel</h3>
              <p class="text-sm text-indigo-50">Gunakan kredensial yang telah terdaftar.</p>
            </div>
            <form @submit.prevent="submit" class="space-y-6">
              <div class="space-y-1">
                <InputField label="Username" icon="user" v-model="form.username" />
                <InputError :message="form.errors.username" class="mt-1" />
              </div>
              <div class="space-y-1">
                <InputField label="Password" :type="showPassword ? 'text' : 'password'" icon="key"
                  v-model="form.password" />
                <InputError :message="form.errors.password" class="mt-1" />
              </div>
              <div class="flex items-center justify-between pt-2">
                <label class="flex items-center text-xs font-medium select-none text-indigo-50">
                  <input type="checkbox" v-model="showPassword"
                    class="w-4 h-4 mr-2 transition-colors border rounded bg-white/20 border-indigo-300/40 focus:ring-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400" />
                  Tampilkan Password
                </label>

              </div>
              <div class="pt-4">
                <PrimaryBtn :disabled="form.processing"
                  class="w-full !py-3 !text-sm font-semibold tracking-wide shadow-lg shadow-sky-900/30 hover:shadow-sky-800/40 focus:ring-2 focus:ring-offset-2 focus:ring-sky-400 focus:ring-offset-slate-900">
                  Login</PrimaryBtn>
              </div>
            </form>
            <div class="mt-10 text-[10px] tracking-wider text-center uppercase text-indigo-200">
              &copy; {{ new Date().getFullYear() }} FEB Universitas Negeri Gorontalo. All rights reserved.
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { useForm } from "@inertiajs/vue3";
import InputField from "@/Components/InputField.vue";
import PrimaryBtn from "@/Components/PrimaryBtn.vue";
import InputError from "@/Components/InputError.vue";
import ApplicationLogo from "@/Components/ApplicationLogo.vue";

const form = useForm({
  username: "",
  password: "",
  remember: null,
});
const showPassword = ref(false);

defineProps({
  status: String,
});
const submit = () => {
  form.post(route("adminpanel"), {
    onFinish: () => form.reset("password"),
  });
};
</script>

<style scoped>
/* Additional fine-tuned styling overrides (if needed) */
/* Input styling (fallback if utility classes not applied inside component) */
:deep(input[type='text']),
:deep(input[type='password']) {
  background-color: rgba(255, 255, 255, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #0f172a;
  /* slate-900 */
  transition: all .25s ease;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.15);
}

:deep(input[type='text']::placeholder),
:deep(input[type='password']::placeholder) {
  color: rgba(199, 210, 254, 0.6);
  /* indigo-200 */
}

:deep(input[type='text']:focus),
:deep(input[type='password']:focus) {
  background-color: #fff;
  border-color: #38bdf8;
  /* sky-400 */
  outline: 2px solid rgba(56, 189, 248, 0.5);
  outline-offset: 0;
}

:deep(label span) {
  color: #eef2ff;
  /* indigo-50 */
}

/* Glass card subtle animation */
.backdrop-blur-md {
  animation: fadeIn 0.8s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(12px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Avatar profile animation (letters A I U E O) */
.animate-profile {
  animation: avatarFade 0.7s ease forwards, avatarFloat 7s ease-in-out infinite;
}

@keyframes avatarFade {
  0% {
    opacity: 0;
    transform: translateY(12px) scale(.85) rotate(-6deg);
    filter: blur(4px);
  }

  60% {
    opacity: 1;
    transform: translateY(-2px) scale(1.02) rotate(2deg);
    filter: blur(0);
  }

  100% {
    opacity: 1;
    transform: translateY(0) scale(1) rotate(0deg);
  }
}

@keyframes avatarFloat {

  0%,
  100% {
    transform: translateY(0) rotate(0deg);
  }

  25% {
    transform: translateY(-4px) rotate(2deg);
  }

  50% {
    transform: translateY(2px) rotate(-2deg);
  }

  75% {
    transform: translateY(-3px) rotate(1deg);
  }
}
</style>
